import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getRenderBufferHeight } from '../scene-manager.js';

// Shared-rig contract (see the framing addendum). 'forbidden': the glass is
// lit analytically (a fixed specular source, per-edge rim, a tinted extruded
// rim) and the only other light in frame is the backdrop it refracts. A wash
// cone would flatten exactly the angular response that reads as glass.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'shatter',
  family: 'object',
  // 0.40, not the 0.88 it launched with: the danceability pool picks the
  // profiles NEAREST the track's own index, so this now offers itself to
  // mid/low-danceability tracks rather than competing in the party tier.
  danceability: 0.40,
  // 0, not 1. requiresTier gates SELECTION, not capability: this file has
  // had a real tier-0 layout all along (see its TIER table). The radio page
  // settles at tier 0 on the hardware it actually runs on - measured on
  // judicael.com - so at 1 this was never offered there at all, which is
  // why none of the recent visualizers ever appeared on the web radio. The
  // cost is that weak devices now get it at its tier-0 layout rather than
  // not at all; the frame-time governor still owns the tier itself.
  requiresTier: 0,
  energyLevel: 0.85,
  bpmMin: 100,
  bpmMax: 180,
  movement: 0.75,
  tonality: 0.65,
  prefers: { dynamics: [0.20, 0.85] },
  enabled: true
};

// Shatter: the cover is a pane of glass that bursts in slow motion ON the
// track's own drops, then reassembles before the next one.
//
// WHAT SEPARATES THIS FROM A TRANSITION EFFECT: the drops are known in
// advance. The break is not periodic and not reactive-after-the-fact - it is
// SCHEDULED against the analysis timeline, which is what buys the four beats
// of vibration and the one beat of spreading cracks BEFORE the hit. A
// visualizer that only sees the drop as it arrives can never do the
// anticipation, and the anticipation is most of the effect.
//
// EXCEPTION TO THE NO-RENDER-TARGET CONTRACT. Screen-space refraction needs
// the backdrop as a texture: it is rendered into a FeedbackBuffer target,
// displayed full-frame, and then sampled again - offset per fragment - by
// the glass. FeedbackBuffer is reused here purely as the managed,
// correctly-sized target allocator it already is (see echo.js, its first
// user); there is no feedback loop, so only `write` is ever touched and
// swap() is never called. The passes run inside the display quad's own
// onBeforeRender, the one place a visualizer can legally reach the
// WebGLRenderer - same mechanism and the same save/restore discipline as
// echo.js's, for the same reason.
//
// THE FRACTURE IS PRECALCULATED, NEVER SIMULATED. One Voronoi diagram per
// break cycle, built on the CPU in idle time (see _pumpGeneration): density
// falling off from an off-centre impact, sites biased onto rays so the
// shards point back at it. A uniform-density diagram reads as a mosaic, not
// as broken glass, and that difference is the whole of criterion 1.
//
// THE PHYSICS IS 200 RIGID BODIES, NO COLLISIONS. At this slow motion two
// fragments interpenetrating is invisible, and collision response for 200
// convex polyhedra is not affordable here. Gravity and drag only.

// --- plate / geometry ---------------------------------------------------
// Everything - sites, cell corners, physics - lives in PLATE-NORMALIZED
// units, the pane spanning [-0.5, 0.5] on both axes. uPlateSize scales that
// to world at draw time, so a resize (or a differently-framed cover) costs
// one uniform and never a rebuild, and no tuned constant below is tied to a
// particular framing.
const CAMERA_DIST = 7.0;
// Fraction of the visible frame HEIGHT the intact pane occupies. EXACTLY
// the DOM cover's own `--art-size: min(30vh, 26vw)`, and it must stay that
// way: the pane carries the cover as its texture, so a pane any larger than
// the cover renders a second, bigger copy of the artwork spilling out from
// behind the real one. (Tried at 0.40 to make the pre-drop cracks visible
// past the opaque cover - it produced exactly that duplicate.) The pane
// being hidden behind the cover while intact is not a fault to fix, it IS
// the mise en abyme: the cover you are looking at is the pane, which is why
// it is the cover that appears to shatter.
const PLATE_FRAC_OF_HEIGHT = 0.30;
// The cover does NOT sit at true screen centre: the title and artist below
// it pull the overlay block's own midpoint down, so the image itself rides
// above centre. Same measured figure as safeZone.glsl.js's SAFE_CENTER_Y
// (46.2px above centre at 1080p), in the same "height units" where 1.0 is
// half the frame. Taken as a FIXED constant rather than read live from the
// measured safe zone, for the reason portal.js already carries: the live
// rect moves when lyrics mode reparks the cover, and a pane that jumped
// with it would read as a bug. Without this the pane sat a visible ~50px
// below the cover it is supposed to be.
const PLATE_CENTER_Y_UNITS = 0.0855;
// ...EXCEPT THAT THE COVER IS NOT ALWAYS THAT SIZE (27/09/2026). The /play
// page scales it by the user's own cover-size setting (--art-scale), halves
// it in lyrics mode and moves it to a corner, and 26vw wins over 30vh on a
// narrow screen. With the pane fixed at 30% of the height, a smaller DOM
// cover left the title and artist - laid out under the REAL cover box -
// printed across the pane. The DOM cover is hidden by opacity only, so its
// box is still laid out: the pane now takes its size and place from
// #overlay-artwork's own rect, measured at draw time (at most every
// COVER_MEASURE_MS) and eased over a few frames so a lyrics-mode move glides
// instead of jumping. The two constants above are the fallback when there is
// no such element (a page without the overlay).
const COVER_MEASURE_MS = 200;
const COVER_EASE = 0.25;           // per frame, towards the measured box
const MAX_CORNERS = 8;          // cells are clipped to this; see _buildCellGeometry
const THICKNESS_WORLD = 0.04;   // "un fragment sans epaisseur lit comme du papier"

// --- fracture pattern ----------------------------------------------------
const SITES_MIN = 120;          // progress 0/1
const SITES_MAX = 200;          // mid-track
const IMPACT_OFFSET_MIN = 0.15; // of the plate's own width, off dead centre
const IMPACT_OFFSET_MAX = 0.30;
// Radius sampling: r = Rmax * u^DENSITY_GAMMA. 0.5 would be uniform over the
// AREA; above that pulls sites toward the impact, which is what puts the
// small splinters at the centre and the big plates out at the rim.
const DENSITY_GAMMA = 1.05;
const RADIAL_RAY_COUNT = 18;
// Share of sites snapped onto a ray. A cell is long where its neighbours are
// far: sites packed tightly ACROSS the rays (small angular jitter) and spread
// out ALONG them give cells that are thin angularly and long radially - i.e.
// splinters pointing back at the impact (criterion 2).
const RADIAL_SHARE = 0.45;
const RADIAL_ANGLE_JITTER = 0.018; // radians, small on purpose - see above
// Cells clipped per idle slice. This is a GUARANTEED floor, not a budget
// polled against deadline.timeRemaining(): under this project's own
// continuous rAF render loop the idle deadline is essentially always zero,
// so a slice that yields as soon as timeRemaining() runs out completes about
// one cell per callback and a 200-site diagram takes a minute (measured -
// 11 of 120 sites after six seconds). 24 cells is ~1-2ms, comfortably
// inside one frame, so the floor keeps criterion 8 while still finishing in
// well under a second.
const GEN_SITES_PER_SLICE = 24;
const GEN_SITES_PER_SLICE_MAX = 64;  // opportunistic extra when idle time IS available
const GEN_IDLE_TIMEOUT_MS = 60;

// --- physics -------------------------------------------------------------
const FIXED_DT = 1 / 60;
const MAX_SUBSTEPS = 3;
const GRAVITY = -0.55;          // plate units/s^2
const DRAG = 0.34;              // per second, linear and angular
const EJECT_SPEED = 0.95;       // plate units/s at unit mass
const EJECT_TOWARD_CAMERA = 0.42;
const SPIN_PER_SPEED = 7.5;     // rad/s per unit linear speed
const DYNAMICS_EJECT_MIN = 0.7; // character.dynamics maps into this band
const DYNAMICS_EJECT_MAX = 1.3;

// --- slow motion ---------------------------------------------------------
// "C'est le ralenti qui donne le spectaculaire" - at real speed the whole
// burst is over in 300ms and reads as a glitch.
const TIME_SCALE_BURST = 0.12;
const TIME_SCALE_SETTLED = 0.40;
const TIME_SCALE_RAMP_S = 2.6;  // wall-clock seconds from burst to settled

// --- reassembly ----------------------------------------------------------
// Never rewound: a reversed playback is recognised instantly. Each fragment
// interpolates from wherever it actually is to its rest pose on its own
// clock, the outer ones first.
const RETURN_S_NEAR = 4.5;      // fragments born at the impact take longest
const RETURN_S_FAR = 2.5;
// easeOutBack's own overshoot constant. Peak overshoot for c1 is at
// u = -2c1/(3(c1+1)); at c1 = 0.75 that puts the peak at 1.021, i.e. the 2%
// the brief asks for. Without it the arrival reads as soft.
const RETURN_BACK_C1 = 0.75;
const REASSEMBLE_LEAD_BARS = 2; // finished this long before the next drop

// --- scheduling ----------------------------------------------------------
const DROP_SCORE_MIN = 0.7;
const VIBRATE_BEATS = 4;
const CRACK_BEATS = 1;
// The longest the pane may ever stay whole, WHETHER OR NOT the track has an
// analysis. Measured on a real track: it carried exactly one drop scoring
// >= 0.7, 127 SECONDS away - and the first version of this treated "a drop
// is scheduled" as "do nothing until then", so the piece sat perfectly
// still for over two minutes and read as broken. Scheduled drops still take
// priority and still get the full four beats of anticipation; this is only
// the floor that keeps the thing alive between them.
// 14, not the brief's own 25. The brief wrote 25 without a dwell time in
// front of it: a composition in the rotation lasts 15-26s (see
// orchestrator.js's DURATION_MIN_S/MAX_S), and this profile's high
// danceability pulls it toward the SHORT end - so at 25s the pane very
// often left the screen having never broken once, which is exactly how it
// came to read as a static picture. 14s puts at least one break inside
// every appearance, usually two.
const MAX_QUIET_S = 14;
// ...and the FIRST break comes sooner still, so the piece states what it is
// within a few seconds of appearing instead of showing a still cover for a
// quarter of a minute. Only the first: the rate limit above governs the
// rest.
const FIRST_BREAK_S = 5;
// A drop already this far in the past WHEN THE VISUALIZER GETS HERE is skipped
// rather than fired late - switching in mid-track. It is measured against
// the PREVIOUS frame's position, not this one's: the estimated position
// jumps forward when it is re-anchored on a poll, and a drop crossed by such
// a jump used to count as stale and was lost - the pane cracked on the last
// beat and then went back to intact without ever breaking.
const DROP_STALE_MS = 250;
// A drop that fires while the diagram is still being generated waits for it,
// this long at most, instead of being dropped.
const DROP_WAIT_PATTERN_S = 1.5;
// The shortest flight, whatever the next drop asks: under it the break reads
// as a crack closing again.
const MIN_FLIGHT_S = 1.2;
const DEFAULT_BPM = 120;

// --- render --------------------------------------------------------------
const REFRACT = 0.055;          // screen-space offset at unit depth
const DISPERSION = 0.04;        // r/b sample spread around the g sample
const ART_MIX = 0.65;           // "le verre est teinte, pas opaque"
const EDGE_RIM_BASE = 0.35;
const EDGE_RIM_BAND = 0.55;     // scaled by bands.high
const RIM_TINT = [0.62, 1.0, 0.86]; // the green cast of a real glass edge
const SPEC_DIR = [0.42, 0.62, 0.66];
const SAFE_FLOOR = 0.5;
const PEAK_LUMINANCE = 0.92;
// Plate units at full bands.low, and deliberately small. A tremor is the
// pane straining, not the pane moving: at 0.020 it shifted far enough to
// push the artwork out from behind the DOM cover, and at the 6.5Hz it used
// to shake at, that offset strobed against the frame rate and read as the
// whole image juddering. Small amplitude, and slow enough that a 30fps
// panel samples the motion rather than aliasing it.
const VIBRATE_AMP = 0.005;
const VIBRATE_HZ_A = 9.0;   // rad/s, not Hz - about 1.4Hz
const VIBRATE_HZ_B = 7.3;
// The intact pane's own slow drift, in world units / radians.
// Deliberately small. The brief asks the intact pane to "derive doucement",
// but this is now the COVER itself (the DOM copy is hidden), and a cover
// that visibly wanders reads as broken rather than as glass - the more so
// because the chain runs antialias:false at 66% render scale on the Pi, so
// a moving hard edge makes its own stair-steps crawl. Just enough that the
// pane is not a frozen picture.
const DRIFT_SHIFT = 0.030;
const DRIFT_TILT = 0.010;
// Width of the pane's own silhouette fade, in drawing-buffer pixels.
const EDGE_AA_PX = 1.8;
const BACKDROP_SCALE = 0.5;     // refraction is a blur by nature - half res is invisible

const TIER = {
  // requiresTier 1 keeps tier 0 out of the rotation; a live downgrade can
  // still land here, so it renders rather than throwing - just as tier 1.
  0: { sites: SITES_MIN, thickness: false, dispersive: false },
  1: { sites: SITES_MIN, thickness: false, dispersive: false },
  2: { sites: SITES_MAX, thickness: true, dispersive: true }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function clamp(x, lo, hi) { return Math.max(lo, Math.min(hi, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function urlNumber(name) {
  const v = urlParam(name);
  return v == null ? NaN : parseFloat(v);
}

// ============================================================================
// Fracture pattern. Plain data in, plain data out - no THREE, no GL - so the
// whole thing can run in slices inside requestIdleCallback.
// ============================================================================

// Sutherland-Hodgman against ONE half-plane: keep the side of the bisector
// of (a,b) that contains a. Returns a new polygon.
function clipToBisector(poly, ax, ay, bx, by) {
  // Bisector: points p with dot(p - mid, d) <= 0 are on a's side, d = b - a.
  const dx = bx - ax, dy = by - ay;
  const mx = (ax + bx) * 0.5, my = (ay + by) * 0.5;
  const out = [];
  const n = poly.length / 2;
  for (let i = 0; i < n; i++) {
    const px = poly[i * 2], py = poly[i * 2 + 1];
    const qx = poly[((i + 1) % n) * 2], qy = poly[((i + 1) % n) * 2 + 1];
    const dp = (px - mx) * dx + (py - my) * dy;
    const dq = (qx - mx) * dx + (qy - my) * dy;
    const pIn = dp <= 0, qIn = dq <= 0;
    if (pIn) out.push(px, py);
    if (pIn !== qIn) {
      const t = dp / (dp - dq);
      out.push(px + (qx - px) * t, py + (qy - py) * t);
    }
  }
  return out;
}

function polygonArea(poly) {
  let a = 0;
  const n = poly.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    a += poly[i * 2] * poly[j * 2 + 1] - poly[j * 2] * poly[i * 2 + 1];
  }
  return Math.abs(a) * 0.5;
}

function polygonCentroid(poly) {
  let cx = 0, cy = 0, a = 0;
  const n = poly.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const cross = poly[i * 2] * poly[j * 2 + 1] - poly[j * 2] * poly[i * 2 + 1];
    a += cross;
    cx += (poly[i * 2] + poly[j * 2]) * cross;
    cy += (poly[i * 2 + 1] + poly[j * 2 + 1]) * cross;
  }
  if (Math.abs(a) < 1e-9) return [poly[0], poly[1]];
  return [cx / (3 * a), cy / (3 * a)];
}

// Keeps the MAX_CORNERS most significant corners, dropping the shortest
// edges first - a Voronoi cell routinely has a couple of near-duplicate
// vertices where three bisectors almost meet, and those cost a slot each
// without changing the silhouette.
function reduceCorners(poly, maxCorners) {
  let pts = [];
  const n = poly.length / 2;
  for (let i = 0; i < n; i++) pts.push([poly[i * 2], poly[i * 2 + 1]]);
  while (pts.length > maxCorners) {
    let best = 0, bestLen = Infinity;
    for (let i = 0; i < pts.length; i++) {
      const j = (i + 1) % pts.length;
      const dx = pts[j][0] - pts[i][0], dy = pts[j][1] - pts[i][1];
      const len = dx * dx + dy * dy;
      if (len < bestLen) { bestLen = len; best = j; }
    }
    pts.splice(best, 1);
  }
  return pts;
}

// Sites: dense at the impact, sparse at the rim, a share of them snapped
// onto rays. See DENSITY_GAMMA / RADIAL_SHARE.
function buildSites(count, impactX, impactY, rand) {
  const rays = [];
  for (let i = 0; i < RADIAL_RAY_COUNT; i++) rays.push(rand() * Math.PI * 2);
  const sites = new Float32Array(count * 2);
  // Every site must land INSIDE the pane. A site outside it has its whole
  // cell clipped away and is simply lost - measured, ~35% of them were, so
  // a 120-site diagram delivered 78 fragments. Rejection-resampling keeps
  // the density profile intact (unlike clamping, which would pile the
  // rejects onto the border) and makes the fragment count exact.
  const LIMIT = 0.49;
  const rMax = 0.95;
  for (let i = 0; i < count; i++) {
    let x = impactX, y = impactY;
    // Site 0 sits ON the impact so the splinter pattern has a real centre.
    if (i > 0) {
      let placed = false;
      for (let attempt = 0; attempt < 24 && !placed; attempt++) {
        const r = rMax * Math.pow(rand(), DENSITY_GAMMA);
        const a = rand() < RADIAL_SHARE
          ? rays[(rand() * RADIAL_RAY_COUNT) | 0] + (rand() - 0.5) * 2 * RADIAL_ANGLE_JITTER
          : rand() * Math.PI * 2;
        x = impactX + Math.cos(a) * r;
        y = impactY + Math.sin(a) * r;
        placed = x >= -LIMIT && x <= LIMIT && y >= -LIMIT && y <= LIMIT;
      }
      if (!placed) {
        // Exhausted the tries (only reachable for an extreme impact offset):
        // anywhere inside beats losing the fragment.
        x = (rand() * 2 - 1) * LIMIT;
        y = (rand() * 2 - 1) * LIMIT;
      }
    }
    sites[i * 2] = x;
    sites[i * 2 + 1] = y;
  }
  return sites;
}

// ============================================================================
// Shaders
// ============================================================================

const SAFE_ZONE_CHUNK = `
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeZoneAttenuation(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

const fullscreenVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// The backdrop has to carry STRUCTURE: refraction is only visible in what it
// displaces, and a flat wash displaced by anything at all still looks flat.
const backdropFragmentShader = `
  precision mediump float;
  uniform vec3 uTint;
  uniform float uAspect;
  uniform float uTime;
  varying vec2 vUv;

  float hash12(vec2 p) {
    // Inputs bounded before the trig: on the TV's real fp16 mediump an
    // unbounded fract(sin(dot(...)) * big) overflows to NaN.
    vec2 q = fract(p * 0.31830988);
    return fract(sin(dot(q, vec2(12.9898, 78.233))) * 43758.5453);
  }

  void main() {
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
    float r = length(p);

    // Base: a deep radial gradient in the cover's own dominant colour.
    vec3 col = uTint * (0.05 + 0.20 * (1.0 - clamp(r * 1.05, 0.0, 1.0)));

    // Slow luminous blobs - the contrast the glass actually bends. Three is
    // enough to give every part of the frame something to displace.
    for (int i = 0; i < 3; i++) {
      float fi = float(i);
      float t = uTime * (0.045 + fi * 0.017) + fi * 2.1;
      vec2 c = vec2(cos(t * 0.9 + fi) * 0.62 * uAspect, sin(t * 1.13 + fi * 2.0) * 0.46);
      float d = length(p - c);
      float glow = exp(-d * d * 7.0);
      col += uTint * glow * 0.42 + vec3(0.06, 0.08, 0.11) * glow;
    }

    // Fine speckle. FIXED in place, with a smooth per-speck twinkle: the
    // first version re-rolled the whole field on floor(uTime * 1.7), so
    // every speck jumped to a new position twice a second and the layer
    // read as dots stuttering across the screen rather than as a texture
    // for the glass to refract. The cell hash is the speck's identity now,
    // not a per-frame draw, and only its brightness moves.
    vec2 cell = floor(vUv * 420.0);
    float sp = hash12(cell);
    float speck = smoothstep(0.9975, 1.0, sp);
    float twinkle = 0.62 + 0.38 * sin(uTime * 1.25 + sp * 6.2831853);
    col += vec3(speck * twinkle) * 0.5;
    col += (hash12(gl_FragCoord.xy) - 0.5) / 255.0;

    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

const displayFragmentShader = `
  precision mediump float;
  uniform sampler2D uBackdrop;
  varying vec2 vUv;
  void main() {
    gl_FragColor = vec4(texture2D(uBackdrop, vUv).rgb, 1.0);
  }
`;

// Fragments. One InstancedBufferGeometry: a fixed 8-corner template drawn
// once per fragment, with each fragment's own corner ring supplied as four
// instanced vec4s (two corners each). A cell with fewer than 8 corners
// repeats its last one, which collapses the spare fan triangles and side
// quads to zero area - cheaper than a second geometry per corner count and
// it keeps the whole pane in ONE draw call.
function buildFragmentVertexShader() {
  return `
    precision mediump float;

    attribute float aCornerIdx;   // 0..7, or 8 for the fan's centre vertex
    attribute float aZSign;       // +1 front face, -1 back of the extrusion
    attribute float aIsSide;      // 1 on the extruded rim band
    // The rim band's own edge, carried separately from aCornerIdx: all six
    // vertices of one rim quad must derive the SAME outward normal, and two
    // of them sit on the next corner along - reading the normal off each
    // vertex's own corner would give one quad three different normals.
    attribute float aEdgeBase;

    attribute vec4 aCorn01;
    attribute vec4 aCorn23;
    attribute vec4 aCorn45;
    attribute vec4 aCorn67;
    attribute vec2 aCentroid;     // cell centroid, plate space
    attribute vec3 aPos;          // live centroid position, plate space
    attribute vec4 aQuat;         // live orientation
    attribute vec3 aMeta;         // x: normalised distance to impact, y: seed, z: area

    uniform float uPlateSize;     // plate-normalised -> world
    uniform float uThickness;     // in plate-normalised units
    uniform float uCrack;         // 0 intact .. 1 fully cracked: opens the seams
    uniform float uVibrate;       // plate-space jitter amplitude
    uniform float uTime;

    varying vec2 vUv;
    varying vec3 vNormalW;
    varying float vDepth;
    varying float vIsSide;
    varying float vEdge;          // 0 at the cell's centre, 1 at its rim
    varying float vDist;
    varying float vSafe;

    ${SAFE_ZONE_CHUNK}

    vec2 cornerAt(float idx) {
      if (idx < 0.5) return aCorn01.xy;
      if (idx < 1.5) return aCorn01.zw;
      if (idx < 2.5) return aCorn23.xy;
      if (idx < 3.5) return aCorn23.zw;
      if (idx < 4.5) return aCorn45.xy;
      if (idx < 5.5) return aCorn45.zw;
      if (idx < 6.5) return aCorn67.xy;
      if (idx < 7.5) return aCorn67.zw;
      return vec2(0.0);           // fan centre
    }

    vec3 rotByQuat(vec3 v, vec4 q) {
      return v + 2.0 * cross(q.xyz, cross(q.xyz, v) + q.w * v);
    }

    void main() {
      float isCentre = step(7.5, aCornerIdx);
      vec2 c0 = cornerAt(aCornerIdx);
      // The rim quad's own edge, not this vertex's corner - see aEdgeBase.
      vec2 e0 = cornerAt(aEdgeBase);
      vec2 e1 = cornerAt(mod(aEdgeBase + 1.0, 8.0));

      // The seam: corners pull very slightly toward their own centroid as
      // the pane cracks, which opens a hairline between neighbours without
      // moving any fragment. Intact (uCrack 0) the cells still tile exactly.
      vec2 local2 = mix(c0 * (1.0 - uCrack * 0.02), vec2(0.0), isCentre);

      vec3 local = vec3(local2, aZSign * uThickness * 0.5);

      // Rim normal: perpendicular to this edge, pushed outward. A repeated
      // corner gives a zero-length edge, so fall back to the radial
      // direction rather than normalising zero.
      vec3 nLocal = vec3(0.0, 0.0, aZSign);
      if (aIsSide > 0.5) {
        vec2 e = e1 - e0;
        vec2 n2 = length(e) > 1e-5 ? normalize(vec2(e.y, -e.x)) : normalize(e0 + vec2(1e-5));
        if (dot(n2, e0) < 0.0) n2 = -n2;
        nLocal = vec3(n2, 0.0);
      }

      vec3 rotated = rotByQuat(local, aQuat);
      vec3 pos = aPos + rotated;

      // Anticipation tremor - the pane shakes, nothing detaches yet.
      if (uVibrate > 0.0) {
        float s = aMeta.y * 6.2831853;
        pos.xy += vec2(sin(uTime * ${VIBRATE_HZ_A.toFixed(1)} + s), cos(uTime * ${VIBRATE_HZ_B.toFixed(1)} + s * 1.7)) * uVibrate;
      }

      // LOCAL to the mesh, not world: the mesh itself is parked in front of
      // the camera every frame (see _placePlate), which is what keeps the
      // pane registered with the DOM cover.
      vec3 world = vec3(pos.xy * uPlateSize, pos.z * uPlateSize);

      // UV is the corner's position on the INTACT pane, so every fragment
      // carries its own portion of the cover wherever it ends up.
      vUv = (aCentroid + local2) + 0.5;

      vNormalW = normalize(rotByQuat(nLocal, aQuat));
      vIsSide = aIsSide;
      vEdge = 1.0 - isCentre;
      vDist = aMeta.x;

      vec4 mv = modelViewMatrix * vec4(world, 1.0);
      vDepth = max(-mv.z, 0.001);
      vec4 clip = projectionMatrix * mv;
      gl_Position = clip;
      vSafe = safeZoneAttenuation(clip);
    }
  `;
}

function buildFragmentFragmentShader(layout) {
  // One sample at tier 1, three at tier 2. The chromatic spread is what
  // reads as glass rather than plastic (criterion 5), and it is also the
  // most expensive thing in this shader - hence the tier split.
  const refractBody = layout.dispersive
    ? `
      vec3 refr;
      refr.r = texture2D(uBackdrop, uvScreen + offset * ${(1.0 + DISPERSION).toFixed(3)}).r;
      refr.g = texture2D(uBackdrop, uvScreen + offset).g;
      refr.b = texture2D(uBackdrop, uvScreen + offset * ${(1.0 - DISPERSION).toFixed(3)}).b;
    `
    : `
      vec3 refr = texture2D(uBackdrop, uvScreen + offset).rgb;
    `;

  return `
    precision mediump float;

    uniform sampler2D uBackdrop;
    uniform sampler2D uArt;
    uniform float uHasArt;
    uniform vec2 uResolution;
    uniform float uRefract;
    uniform float uEdgeRim;
    uniform float uEdgeAA;
    uniform float uBeatPhase;  // beatPhase, for the wave crossing the shards
    uniform float uWaveAmt;    // 0 unless in flight - gates the wave entirely
    uniform vec3 uGlassTint;

    varying vec2 vUv;
    varying vec3 vNormalW;
    varying float vDepth;
    varying float vIsSide;
    varying float vEdge;
    varying float vDist;
    varying float vSafe;

    void main() {
      vec3 N = normalize(vNormalW);
      // Only the front face is built (no back cap), so a tumbling fragment
      // shows it from behind - without this its normal points away and the
      // specular and rim both invert.
      if (!gl_FrontFacing) N = -N;
      vec2 uvScreen = gl_FragCoord.xy / uResolution;

      // Screen-space refraction: the steeper the facet, the further it
      // displaces what is behind it, and the further away it is the less.
      vec2 offset = N.xy * uRefract / max(vDepth, 1.0);
      ${refractBody}

      vec3 col = refr * uGlassTint;

      // The cover's own portion of the image, mixed OVER the refraction at
      // ART_MIX - "le verre est teinte, pas opaque", so what is behind is
      // still visible through it.
      if (uHasArt > 0.5) {
        vec3 art = texture2D(uArt, clamp(vUv, 0.0, 1.0)).rgb;
        col = mix(col, art, ${ART_MIX.toFixed(2)});
      }

      // The extruded rim: darker, and green like the cut edge of real glass.
      if (vIsSide > 0.5) {
        col *= vec3(${RIM_TINT[0].toFixed(2)}, ${RIM_TINT[1].toFixed(2)}, ${RIM_TINT[2].toFixed(2)}) * 0.55;
      }

      // Hard specular off the front face, fixed source.
      vec3 L = normalize(vec3(${SPEC_DIR[0].toFixed(2)}, ${SPEC_DIR[1].toFixed(2)}, ${SPEC_DIR[2].toFixed(2)}));
      float spec = pow(max(dot(N, L), 0.0), 48.0);
      col += vec3(1.0, 0.99, 0.96) * spec * 0.55 * (1.0 - vIsSide * 0.6);

      // THE edge rim. A sharp edge catches the light according to how it is
      // turned to the source, which is the most characteristic single detail
      // of broken glass (criterion 6) - so this is an angular term, not a
      // flat outline.
      // pow() so the rim is a thin liseré on the cell's own boundary rather
      // than a broad gradient out of its middle: vEdge interpolates linearly
      // from the fan's centre vertex, so it needs shaping to read as an edge.
      float facing = abs(dot(N, L));
      float rim = pow(vEdge, 7.0) * uEdgeRim * (0.35 + 0.65 * facing);
      col += vec3(0.85, 0.93, 1.0) * rim;

      // In flight, a brightness wave crosses the fragments in the order of
      // their distance from the impact. min(ph, 1-ph) so the pulse wraps at
      // the beat boundary instead of cutting; uWaveAmt gates it off entirely
      // when nothing is flying (at rest the phase term would otherwise sit
      // permanently lit on the fragments nearest the impact).
      float ph = fract(uBeatPhase - vDist * 0.6);
      float wave = smoothstep(0.13, 0.0, min(ph, 1.0 - ph));
      col += vec3(0.5, 0.62, 0.8) * wave * uWaveAmt * 0.45;

      // Antialias the pane's OUTER silhouette. The whole chain runs
      // antialias:false and, on the Pi, at 66% render scale (see
      // scene-manager's BASE_RENDER_SCALE x TIER_RESOLUTION_SCALE), so the
      // pane's hard square edge stair-steps badly - it used to be hidden
      // behind the opaque DOM cover, and exposing the glass as the cover
      // exposed it too. Only the boundary of the PANE is softened, never
      // the seams between neighbouring cells: those tile exactly while
      // intact and fading them would draw cracks across an unbroken pane.
      float edgeDist = min(min(vUv.x, 1.0 - vUv.x), min(vUv.y, 1.0 - vUv.y));
      col = mix(refr, col, smoothstep(0.0, uEdgeAA, edgeDist));

      col *= vSafe;

      float mx = max(col.r, max(col.g, col.b));
      if (mx > ${PEAK_LUMINANCE.toFixed(2)}) col *= ${PEAK_LUMINANCE.toFixed(2)} / mx;

      gl_FragColor = vec4(col, 1.0);
    }
  `;
}

export class ShatterVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];

    this._debugCells = urlParam('shattercells') === '1';
    this._debugInfo = urlParam('shatterinfo') === '1';
    this._forceBreak = urlParam('shatterbreak') === '1';
    const ts = urlNumber('shattertime');
    this._forcedTimeScale = Number.isFinite(ts) ? ts : null;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(0, 0, CAMERA_DIST);
    camera.lookAt(0, 0, 0);

    this._recomputeWorld();

    // --- backdrop, into a managed target (see the header on FeedbackBuffer) ---
    this._buffer = new FeedbackBuffer({ scale: BACKDROP_SCALE });
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 1);
    this._backdropUniforms = {
      uTint: { value: new THREE.Color(0x2a3550) },
      uAspect: { value: camera.aspect },
      uTime: { value: 0 }
    };
    this._backdropScene = new THREE.Scene();
    this._backdropGeo = new THREE.PlaneGeometry(1, 1);
    this._backdropMat = new THREE.ShaderMaterial({
      uniforms: this._backdropUniforms,
      vertexShader: fullscreenVertexShader,
      fragmentShader: backdropFragmentShader,
      depthTest: false,
      depthWrite: false
    });
    const backdropMesh = new THREE.Mesh(this._backdropGeo, this._backdropMat);
    backdropMesh.frustumCulled = false;
    this._backdropScene.add(backdropMesh);

    // --- the full-frame display of that backdrop, in the MAIN scene ---
    this._displayUniforms = { uBackdrop: { value: null } };
    this._displayGeo = new THREE.PlaneGeometry(1, 1);
    this._displayMat = new THREE.ShaderMaterial({
      uniforms: this._displayUniforms,
      vertexShader: fullscreenVertexShader,
      fragmentShader: displayFragmentShader,
      depthTest: false,
      depthWrite: false
    });
    this._displayMesh = new THREE.Mesh(this._displayGeo, this._displayMat);
    this._displayMesh.frustumCulled = false;
    this._displayMesh.renderOrder = -1000;
    const self = this;
    this._displayMesh.onBeforeRender = function (renderer) { self._runPasses(renderer); };
    scene.add(this._displayMesh);

    this._buildFragmentMesh();

    // --- state ---
    this._time = 0;            // wall clock, freezes on pause
    this._simTime = 0;         // slow-motion clock the physics integrates on
    this._accumulator = 0;
    this._returnAccumulator = 0;
    this._paused = false;
    this._phase = 'intact';
    this._phaseStart = 0;
    this._burstAt = -1e9;
    // NOT -1e9: the no-analysis fallback measures the next break from this,
    // so a sentinel in the far past puts "time until the next break" at
    // minus infinity, which pins the anticipation phase to 'crack' on the
    // very first frame - and 'crack' is not 'intact', so the first diagram
    // could never be adopted either and the pane stayed empty forever.
    // Starting the clock at zero means the first fallback break is one
    // full gap away, with the pane whole and the anticipation intact.
    this._lastBreakAt = -(MAX_QUIET_S - FIRST_BREAK_S);
    this._lastTrackId = undefined;
    this._lastArtworkUrl = null;
    this._lastPaletteRef = undefined;
    this._lastLow = 0;
    this._timeScale = TIME_SCALE_SETTLED;
    this._pattern = null;
    this._pendingPattern = null;
    this._genState = null;
    this._idleHandle = null;
    this._nextDropMs = null;
    this._artTexture = null;
    this._marksRef = undefined;
    this._drops = [];
    this._dropIdx = 0;
    this._dropDueAt = null;
    this._lastNowMs = null;
    this._nowMs = null;

    this._requestPattern(SITES_MIN);
  }

  _recomputeWorld() {
    const fov = THREE.MathUtils.degToRad(this._camera.fov || 68);
    this._visibleHeight = 2 * CAMERA_DIST * Math.tan(fov / 2);
    this._plateSize = this._visibleHeight * PLATE_FRAC_OF_HEIGHT;
    this._plateCenterY = PLATE_CENTER_Y_UNITS * (this._visibleHeight / 2);
    // Re-measured and snapped (no easing) on the next draw: a resize is not a
    // move the eye should follow.
    this._plateX = null;
    this._coverMeasuredAt = null;
    this._plateSnap = true;
  }

  // ------------------------------------------------------------------
  // Fracture generation. Sliced across idle callbacks: a 200-site diagram
  // is a few tens of milliseconds of clipping, which inside a frame would
  // be a visible hitch (criterion 8).
  // ------------------------------------------------------------------
  _requestPattern(siteCount) {
    const count = Math.max(16, Math.min(this._layout.sites, siteCount | 0));
    // Deterministic per attempt, but different every time - the pattern is
    // meant to differ between breaks.
    let seed = (Math.random() * 1e9) | 0;
    const rand = function () {
      seed = (seed * 1664525 + 1013904223) | 0;
      return ((seed >>> 8) & 0xffffff) / 0x1000000;
    };
    const ang = rand() * Math.PI * 2;
    const off = lerp(IMPACT_OFFSET_MIN, IMPACT_OFFSET_MAX, rand());
    const impactX = Math.cos(ang) * off;
    const impactY = Math.sin(ang) * off;

    this._genState = {
      count: count,
      impactX: impactX,
      impactY: impactY,
      sites: buildSites(count, impactX, impactY, rand),
      i: 0,
      cells: []
    };
    this._scheduleIdle();
  }

  _scheduleIdle() {
    if (this._idleHandle != null || !this._genState) return;
    const self = this;
    const run = function (deadline) {
      self._idleHandle = null;
      self._pumpGeneration(deadline);
    };
    if (typeof requestIdleCallback === 'function') {
      this._idleHandle = requestIdleCallback(run, { timeout: GEN_IDLE_TIMEOUT_MS });
    } else {
      this._idleHandle = setTimeout(function () { run(null); }, 0);
    }
  }

  _pumpGeneration(deadline) {
    const g = this._genState;
    if (!g) return;
    const sites = g.sites;
    const n = g.count;
    const plate = [-0.5, -0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5];

    let processed = 0;
    while (g.i < n) {
      const ax = sites[g.i * 2], ay = sites[g.i * 2 + 1];
      let poly = plate.slice();
      // Nearest-first with an early out: once no remaining site can reach
      // the polygon (its bisector is further than the polygon's own
      // radius), the cell is final. Without this the diagram is a flat
      // O(n^2) of full clips.
      const order = [];
      for (let j = 0; j < n; j++) {
        if (j === g.i) continue;
        const dx = sites[j * 2] - ax, dy = sites[j * 2 + 1] - ay;
        order.push([dx * dx + dy * dy, j]);
      }
      order.sort(function (p, q) { return p[0] - q[0]; });
      for (let k = 0; k < order.length; k++) {
        const d2 = order[k][0];
        let maxR2 = 0;
        for (let v = 0; v < poly.length; v += 2) {
          const vx = poly[v] - ax, vy = poly[v + 1] - ay;
          const r2 = vx * vx + vy * vy;
          if (r2 > maxR2) maxR2 = r2;
        }
        // Bisector sits at half the distance; nothing beyond 2*polygon
        // radius can cut it.
        if (d2 > 4 * maxR2) break;
        const j = order[k][1];
        poly = clipToBisector(poly, ax, ay, sites[j * 2], sites[j * 2 + 1]);
        if (poly.length < 6) break;
      }
      if (poly.length >= 6) {
        const area = polygonArea(poly);
        if (area > 1e-6) {
          const c = polygonCentroid(poly);
          const pts = reduceCorners(poly, MAX_CORNERS);
          g.cells.push({ centroid: c, corners: pts, area: area });
        }
      }
      g.i++;
      processed++;
      // The floor is unconditional; past it, keep going only while there is
      // real idle time left. See GEN_SITES_PER_SLICE.
      if (processed >= GEN_SITES_PER_SLICE_MAX) break;
      if (processed >= GEN_SITES_PER_SLICE) {
        if (!deadline || typeof deadline.timeRemaining !== 'function' || deadline.timeRemaining() < 2) break;
      }
    }

    if (g.i >= n) {
      this._pendingPattern = { cells: g.cells, impactX: g.impactX, impactY: g.impactY };
      this._genState = null;
      // Only ever adopted while the pane is whole - see _adoptPendingPattern.
      this._adoptPendingPattern();
    } else {
      this._scheduleIdle();
    }
  }

  // A new diagram may only replace the live one when the pane is INTACT: an
  // unbroken pane looks identical however it is subdivided (no seams are
  // drawn), so the swap is invisible. Mid-flight it would teleport every
  // fragment.
  _adoptPendingPattern() {
    if (!this._pendingPattern) return;
    // The phase gate protects fragments already in flight from being
    // teleported onto a different diagram - but when there is no diagram at
    // all yet there is nothing to protect, and refusing the first one would
    // leave the pane permanently empty.
    if (this._pattern && this._phase !== 'intact') return;
    this._pattern = this._pendingPattern;
    this._pendingPattern = null;
    this._uploadPattern();
  }

  // ------------------------------------------------------------------
  // Geometry / instances
  // ------------------------------------------------------------------
  _buildFragmentMesh() {
    const layout = this._layout;
    const maxInstances = layout.sites;

    // Template: a fan over 8 corners (6 triangles), plus - with thickness -
    // an 8-quad rim band. Corner 8 is the fan's centre.
    const cornerIdx = [];
    const zSign = [];
    const isSide = [];
    const edgeBase = [];
    const pushTri = function (a, b, c, z, side, edge) {
      cornerIdx.push(a, b, c);
      zSign.push(z, z, z);
      isSide.push(side, side, side);
      edgeBase.push(edge, edge, edge);
    };
    // Front fan: one triangle per edge of the 8-ring, from the centroid.
    for (let i = 0; i < MAX_CORNERS; i++) {
      pushTri(8, i, (i + 1) % MAX_CORNERS, 1, 0, 0);
    }
    if (layout.thickness) {
      for (let i = 0; i < MAX_CORNERS; i++) {
        const j = (i + 1) % MAX_CORNERS;
        // The rim quad over edge i: front i, front j, back j / front i, back
        // j, back i. Every one of the six carries aEdgeBase = i so they all
        // derive the same outward normal.
        pushTri(i, j, j, 0, 1, i);
        zSign[zSign.length - 3] = 1; zSign[zSign.length - 2] = 1; zSign[zSign.length - 1] = -1;
        pushTri(i, j, i, 0, 1, i);
        zSign[zSign.length - 3] = 1; zSign[zSign.length - 2] = -1; zSign[zSign.length - 1] = -1;
      }
    }

    const geo = new THREE.InstancedBufferGeometry();
    const vertexCount = cornerIdx.length;
    // A real `position` attribute is required by three's own bookkeeping
    // even though the vertex shader never reads it.
    geo.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(vertexCount * 3), 3));
    geo.setAttribute('aCornerIdx', new THREE.Float32BufferAttribute(new Float32Array(cornerIdx), 1));
    geo.setAttribute('aZSign', new THREE.Float32BufferAttribute(new Float32Array(zSign), 1));
    geo.setAttribute('aIsSide', new THREE.Float32BufferAttribute(new Float32Array(isSide), 1));
    geo.setAttribute('aEdgeBase', new THREE.Float32BufferAttribute(new Float32Array(edgeBase), 1));

    this._corn01 = new Float32Array(maxInstances * 4);
    this._corn23 = new Float32Array(maxInstances * 4);
    this._corn45 = new Float32Array(maxInstances * 4);
    this._corn67 = new Float32Array(maxInstances * 4);
    this._centroidArr = new Float32Array(maxInstances * 2);
    this._posArr = new Float32Array(maxInstances * 3);
    this._quatArr = new Float32Array(maxInstances * 4);
    this._metaArr = new Float32Array(maxInstances * 3);

    geo.setAttribute('aCorn01', new THREE.InstancedBufferAttribute(this._corn01, 4));
    geo.setAttribute('aCorn23', new THREE.InstancedBufferAttribute(this._corn23, 4));
    geo.setAttribute('aCorn45', new THREE.InstancedBufferAttribute(this._corn45, 4));
    geo.setAttribute('aCorn67', new THREE.InstancedBufferAttribute(this._corn67, 4));
    geo.setAttribute('aCentroid', new THREE.InstancedBufferAttribute(this._centroidArr, 2));
    geo.setAttribute('aPos', new THREE.InstancedBufferAttribute(this._posArr, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aQuat', new THREE.InstancedBufferAttribute(this._quatArr, 4).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aMeta', new THREE.InstancedBufferAttribute(this._metaArr, 3));
    geo.instanceCount = 0;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 40);

    this._fragUniforms = Object.assign({}, {
      uSafeAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather
    }, {
      uPlateSize: { value: this._plateSize },
      uThickness: { value: layout.thickness ? THICKNESS_WORLD / this._plateSize : 0 },
      uCrack: { value: 0 },
      uVibrate: { value: 0 },
      uTime: { value: 0 },
      uBackdrop: { value: null },
      uArt: { value: null },
      uHasArt: { value: 0 },
      uResolution: { value: new THREE.Vector2(1280, 720) },
      uRefract: { value: REFRACT },
      uEdgeRim: { value: 0 },
      uEdgeAA: { value: 0.01 },
      uBeatPhase: { value: 0 },
      uWaveAmt: { value: 0 },
      uGlassTint: { value: new THREE.Color(0xdfe9ff) }
    });

    this._fragMat = new THREE.ShaderMaterial({
      uniforms: this._fragUniforms,
      vertexShader: buildFragmentVertexShader(),
      fragmentShader: buildFragmentFragmentShader(layout),
      side: THREE.DoubleSide,
      transparent: false,
      depthWrite: true,
      depthTest: true
    });
    this._fragGeo = geo;
    this._fragMesh = new THREE.Mesh(geo, this._fragMat);
    this._fragMesh.frustumCulled = false;
    this._fragMesh.renderOrder = 0;
    this._fragMesh.matrixAutoUpdate = false;
    // Placed from the camera at DRAW time, not in update(). scene-manager's
    // frame order is update() -> _applyCameraDrift() -> render(), so a pane
    // positioned during update() is drawn through a camera that has since
    // moved by up to 0.22 world units - about 17px here - and it visibly
    // slides against the DOM cover it is supposed to be exactly behind.
    // three computes modelViewMatrix AFTER onBeforeRender, so parking the
    // mesh here registers it to the very camera transform used for this
    // draw, and the drift then moves pane and camera together and cancels.
    const selfMesh = this;
    this._fragMesh.onBeforeRender = function (renderer, scene, cam) { selfMesh._placePlate(cam, renderer); };
    this._scene.add(this._fragMesh);
  }

  // The DOM cover is hidden for this visualizer's whole life, not just
  // during the break: the pane RENDERS the cover, so a DOM copy on top
  // means the artwork you are looking at can never break, drift or crack -
  // which is precisely how this read as "a static cover with some glass
  // around it". Title and artist stay (see scene-manager's own
  // hideOverlayArtwork).
  get hideOverlayArtwork() {
    return true;
  }

  // The DOM cover's box, in world units on the pane's plane: { size, x, y }
  // (x right, y up, from the frame's centre), or null when there is none to
  // follow.
  _measureCover(renderer) {
    if (typeof document === 'undefined' || !renderer || !renderer.domElement) return null;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (this._coverMeasuredAt != null && now - this._coverMeasuredAt < COVER_MEASURE_MS) return this._cover;
    this._coverMeasuredAt = now;
    const art = document.getElementById('overlay-artwork');
    this._cover = null;
    if (!art) return null;
    const r = art.getBoundingClientRect();
    const c = renderer.domElement.getBoundingClientRect();
    if (r.width < 4 || r.height < 4 || c.height < 4) return null;
    const halfH = this._visibleHeight / 2, halfPx = c.height / 2;
    this._cover = {
      size: (r.height / c.height) * this._visibleHeight,
      x: ((r.left + r.width / 2) - (c.left + c.width / 2)) / halfPx * halfH,
      y: ((c.top + c.height / 2) - (r.top + r.height / 2)) / halfPx * halfH
    };
    return this._cover;
  }

  _placePlate(cam, renderer) {
    // Size and place from the real DOM cover when there is one, eased.
    const cover = this._measureCover(renderer);
    const target = cover || { size: this._visibleHeight * PLATE_FRAC_OF_HEIGHT, x: 0, y: PLATE_CENTER_Y_UNITS * (this._visibleHeight / 2) };
    if (this._plateX == null || this._plateSnap) {
      this._plateSize = target.size; this._plateX = target.x; this._plateCenterY = target.y; this._plateSnap = false;
    } else {
      this._plateSize += (target.size - this._plateSize) * COVER_EASE;
      this._plateX += (target.x - this._plateX) * COVER_EASE;
      this._plateCenterY += (target.y - this._plateCenterY) * COVER_EASE;
    }
    if (this._fragUniforms) {
      this._fragUniforms.uPlateSize.value = this._plateSize;
      this._fragUniforms.uThickness.value = this._layout.thickness ? THICKNESS_WORLD / this._plateSize : 0;
    }
    cam.updateMatrixWorld();
    const e = cam.matrixWorld.elements;
    _camRight.set(e[0], e[1], e[2]);
    _camUp.set(e[4], e[5], e[6]);
    _camFwd.set(e[8], e[9], e[10]);   // camera's +Z, i.e. backwards
    // "Entre deux eclatements, la plaque est intacte et DERIVE DOUCEMENT."
    // Three incommensurable periods so the sway never repeats on itself.
    // This is what keeps the intact stretches from reading as a still
    // image - the pane is glass hanging in space, not a photograph.
    const t = this._time;
    const bob = Math.sin(t * 0.19) * DRIFT_SHIFT;
    const slide = Math.sin(t * 0.147 + 0.7) * DRIFT_SHIFT;
    this._fragMesh.position.copy(cam.position)
      .addScaledVector(_camFwd, -CAMERA_DIST)
      .addScaledVector(_camUp, this._plateCenterY + bob)
      .addScaledVector(_camRight, this._plateX + slide);
    _driftEuler.set(
      Math.sin(t * 0.17) * DRIFT_TILT,
      Math.sin(t * 0.231 + 1.1) * DRIFT_TILT * 1.4,
      Math.sin(t * 0.129 + 2.0) * DRIFT_TILT * 0.5
    );
    _driftQuat.setFromEuler(_driftEuler);
    this._fragMesh.quaternion.copy(cam.quaternion).multiply(_driftQuat);
    this._fragMesh.updateMatrix();
    this._fragMesh.updateMatrixWorld(true);
  }

  // Writes a finished diagram into the instance attributes and resets every
  // body to rest.
  _uploadPattern() {
    const p = this._pattern;
    if (!p) return;
    const cells = p.cells;
    const n = Math.min(cells.length, this._layout.sites);
    this._count = n;

    this._restX = new Float32Array(n);
    this._restY = new Float32Array(n);
    this._px = new Float32Array(n);
    this._py = new Float32Array(n);
    this._pz = new Float32Array(n);
    this._vx = new Float32Array(n);
    this._vy = new Float32Array(n);
    this._vz = new Float32Array(n);
    this._qx = new Float32Array(n);
    this._qy = new Float32Array(n);
    this._qz = new Float32Array(n);
    this._qw = new Float32Array(n);
    this._wx = new Float32Array(n);
    this._wy = new Float32Array(n);
    this._wz = new Float32Array(n);
    this._area = new Float32Array(n);
    this._dist = new Float32Array(n);
    this._retFrom = new Float32Array(n * 3);
    this._retQuat = new Float32Array(n * 4);
    this._retDur = new Float32Array(n);
    this._retT = new Float32Array(n);

    let maxDist = 1e-5;
    for (let i = 0; i < n; i++) {
      const c = cells[i];
      const dx = c.centroid[0] - p.impactX, dy = c.centroid[1] - p.impactY;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d > maxDist) maxDist = d;
    }

    const cornerTargets = [this._corn01, this._corn23, this._corn45, this._corn67];
    for (let i = 0; i < n; i++) {
      const c = cells[i];
      const pts = c.corners;
      for (let k = 0; k < MAX_CORNERS; k++) {
        const src = pts[Math.min(k, pts.length - 1)];
        const buf = cornerTargets[k >> 1];
        const off = i * 4 + (k & 1) * 2;
        buf[off] = src[0] - c.centroid[0];
        buf[off + 1] = src[1] - c.centroid[1];
      }
      this._centroidArr[i * 2] = c.centroid[0];
      this._centroidArr[i * 2 + 1] = c.centroid[1];
      this._restX[i] = c.centroid[0];
      this._restY[i] = c.centroid[1];
      this._area[i] = c.area;

      const dx = c.centroid[0] - p.impactX, dy = c.centroid[1] - p.impactY;
      this._dist[i] = Math.sqrt(dx * dx + dy * dy) / maxDist;

      this._metaArr[i * 3] = this._dist[i];
      this._metaArr[i * 3 + 1] = (i * 0.61803398875) % 1;
      this._metaArr[i * 3 + 2] = c.area;
    }

    this._resetBodies();
    for (const a of ['aCorn01', 'aCorn23', 'aCorn45', 'aCorn67', 'aCentroid', 'aMeta']) {
      this._fragGeo.attributes[a].needsUpdate = true;
    }
    this._fragGeo.instanceCount = n;
  }

  _resetBodies() {
    const n = this._count || 0;
    for (let i = 0; i < n; i++) {
      this._px[i] = this._restX[i];
      this._py[i] = this._restY[i];
      this._pz[i] = 0;
      this._vx[i] = 0; this._vy[i] = 0; this._vz[i] = 0;
      this._qx[i] = 0; this._qy[i] = 0; this._qz[i] = 0; this._qw[i] = 1;
      this._wx[i] = 0; this._wy[i] = 0; this._wz[i] = 0;
      this._retT[i] = 1;
    }
    this._writeInstances();
  }

  _writeInstances() {
    const n = this._count || 0;
    for (let i = 0; i < n; i++) {
      this._posArr[i * 3] = this._px[i];
      this._posArr[i * 3 + 1] = this._py[i];
      this._posArr[i * 3 + 2] = this._pz[i];
      this._quatArr[i * 4] = this._qx[i];
      this._quatArr[i * 4 + 1] = this._qy[i];
      this._quatArr[i * 4 + 2] = this._qz[i];
      this._quatArr[i * 4 + 3] = this._qw[i];
    }
    this._fragGeo.attributes.aPos.needsUpdate = true;
    this._fragGeo.attributes.aQuat.needsUpdate = true;
  }

  // ------------------------------------------------------------------
  // The break itself
  // ------------------------------------------------------------------
  _burst(dynamics) {
    const n = this._count || 0;
    if (!n || !this._pattern) return;
    const p = this._pattern;
    const speedMul = lerp(DYNAMICS_EJECT_MIN, DYNAMICS_EJECT_MAX, clamp01(dynamics));
    // Mean area, so the impulse is scaled by RELATIVE mass and the burst
    // does not change character with the fragment count.
    let meanArea = 0;
    for (let i = 0; i < n; i++) meanArea += this._area[i];
    meanArea = Math.max(meanArea / n, 1e-6);

    for (let i = 0; i < n; i++) {
      let dx = this._restX[i] - p.impactX;
      let dy = this._restY[i] - p.impactY;
      const d = Math.max(Math.sqrt(dx * dx + dy * dy), 1e-4);
      dx /= d; dy /= d;
      // Lighter shards (smaller area) leave faster - the impulse is shared,
      // the mass is not.
      const massFactor = Math.sqrt(meanArea / Math.max(this._area[i], 1e-6));
      const speed = EJECT_SPEED * speedMul * massFactor * lerp(1.25, 0.65, clamp01(this._dist[i]));
      this._vx[i] = dx * speed;
      this._vy[i] = dy * speed;
      this._vz[i] = EJECT_TOWARD_CAMERA * speedMul * massFactor * (0.55 + Math.random() * 0.9);
      const spin = speed * SPIN_PER_SPEED;
      this._wx[i] = (Math.random() * 2 - 1) * spin;
      this._wy[i] = (Math.random() * 2 - 1) * spin;
      this._wz[i] = (Math.random() * 2 - 1) * spin;
      this._retT[i] = 0;
    }
    this._phase = 'flying';
    this._phaseStart = this._time;
    this._burstAt = this._time;
    this._lastBreakAt = this._time;
  }

  _beginReassembly(windowS) {
    const n = this._count || 0;
    if (!n) return;
    // Durations: the outer fragments come home first. Compressed to fit
    // whatever window is left before the next drop, never stretched past
    // the natural range.
    let maxDur = 0;
    for (let i = 0; i < n; i++) {
      const dur = lerp(RETURN_S_NEAR, RETURN_S_FAR, clamp01(this._dist[i]));
      this._retDur[i] = dur;
      if (dur > maxDur) maxDur = dur;
    }
    if (windowS != null && windowS > 0.5 && windowS < maxDur) {
      const k = windowS / maxDur;
      for (let i = 0; i < n; i++) this._retDur[i] *= k;
    }
    for (let i = 0; i < n; i++) {
      this._retFrom[i * 3] = this._px[i];
      this._retFrom[i * 3 + 1] = this._py[i];
      this._retFrom[i * 3 + 2] = this._pz[i];
      this._retQuat[i * 4] = this._qx[i];
      this._retQuat[i * 4 + 1] = this._qy[i];
      this._retQuat[i * 4 + 2] = this._qz[i];
      this._retQuat[i * 4 + 3] = this._qw[i];
      this._retT[i] = 0;
    }
    this._phase = 'reassembling';
    this._phaseStart = this._time;
    this._returnAccumulator = 0;
  }

  // Semi-implicit Euler on a fixed step - identical at 30 and 60fps
  // (criterion 7).
  _stepPhysics(dt) {
    const n = this._count || 0;
    for (let i = 0; i < n; i++) {
      this._vy[i] += GRAVITY * dt;
      const drag = Math.max(0, 1 - DRAG * dt);
      this._vx[i] *= drag; this._vy[i] *= drag; this._vz[i] *= drag;
      this._wx[i] *= drag; this._wy[i] *= drag; this._wz[i] *= drag;
      this._px[i] += this._vx[i] * dt;
      this._py[i] += this._vy[i] * dt;
      this._pz[i] += this._vz[i] * dt;

      // q += 0.5 * omega * q * dt, renormalised.
      const qx = this._qx[i], qy = this._qy[i], qz = this._qz[i], qw = this._qw[i];
      const ox = this._wx[i], oy = this._wy[i], oz = this._wz[i];
      const h = 0.5 * dt;
      let nx = qx + h * (ox * qw + oy * qz - oz * qy);
      let ny = qy + h * (oy * qw + oz * qx - ox * qz);
      let nz = qz + h * (oz * qw + ox * qy - oy * qx);
      let nw = qw - h * (ox * qx + oy * qy + oz * qz);
      const inv = 1 / Math.max(Math.sqrt(nx * nx + ny * ny + nz * nz + nw * nw), 1e-8);
      this._qx[i] = nx * inv; this._qy[i] = ny * inv; this._qz[i] = nz * inv; this._qw[i] = nw * inv;
    }
  }

  _stepReassembly(dt) {
    const n = this._count || 0;
    let allHome = true;
    const c1 = RETURN_BACK_C1, c3 = c1 + 1;
    for (let i = 0; i < n; i++) {
      if (this._retT[i] >= 1) continue;
      this._retT[i] = Math.min(1, this._retT[i] + dt / Math.max(this._retDur[i], 0.05));
      const t = this._retT[i];
      if (t < 1) allHome = false;
      // easeOutBack: lands, overshoots ~2%, comes back. See RETURN_BACK_C1.
      const u = t - 1;
      const e = 1 + c3 * u * u * u + c1 * u * u;

      this._px[i] = lerp(this._retFrom[i * 3], this._restX[i], e);
      this._py[i] = lerp(this._retFrom[i * 3 + 1], this._restY[i], e);
      this._pz[i] = lerp(this._retFrom[i * 3 + 2], 0, e);

      // Slerp to identity along the short path.
      let ax = this._retQuat[i * 4], ay = this._retQuat[i * 4 + 1],
        az = this._retQuat[i * 4 + 2], aw = this._retQuat[i * 4 + 3];
      if (aw < 0) { ax = -ax; ay = -ay; az = -az; aw = -aw; }
      const dot = clamp(aw, -1, 1);
      const theta = Math.acos(dot);
      const st = Math.sin(theta);
      const et = clamp01(e);
      let s0, s1;
      if (st < 1e-4) { s0 = 1 - et; s1 = et; }
      else { s0 = Math.sin((1 - et) * theta) / st; s1 = Math.sin(et * theta) / st; }
      let rx = ax * s0, ry = ay * s0, rz = az * s0, rw = aw * s0 + s1;
      const inv = 1 / Math.max(Math.sqrt(rx * rx + ry * ry + rz * rz + rw * rw), 1e-8);
      this._qx[i] = rx * inv; this._qy[i] = ry * inv; this._qz[i] = rz * inv; this._qw[i] = rw * inv;
    }
    if (allHome) {
      this._phase = 'intact';
      this._phaseStart = this._time;
      this._resetBodies();
      // A fresh diagram for the next break, now that the pane is whole.
      this._adoptPendingPattern();
      if (!this._pattern || !this._pendingPattern) this._requestPattern(this._wantedSites || SITES_MIN);
    }
  }

  // ------------------------------------------------------------------
  // Scheduling against the timeline
  // ------------------------------------------------------------------
  // The scheduled drops, as a MONOTONIC POINTER rather than a per-frame
  // search for "the next mark still in the future".
  //
  // A search cannot fire reliably: the only moment it reports a drop as
  // imminent is the frame where the remaining time is under the test's own
  // threshold, and one frame at 30fps is 33ms - wider than any sane
  // threshold. Worse, the instant the drop passes, the search stops
  // returning it at all and moves to the next one, so a missed frame means
  // that drop is silently lost forever. The pointer fires on the first
  // frame at or after the drop's own timestamp, whatever the frame rate,
  // and cannot skip one.
  //
  // Returns seconds until the pointed-at drop, or null when none remains.
  _updateDropSchedule(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks;
      this._drops = [];
      if (marks) {
        for (let i = 0; i < marks.length; i++) {
          if (marks[i].type === 'drop' && marks[i].score >= DROP_SCORE_MIN) this._drops.push(marks[i].atMs);
        }
      }
      this._dropIdx = 0;
    }
    // estimatedPosition is in SECONDS; marks carry atMs in MILLISECONDS.
    // Comparing the two directly (the first version did) makes every drop
    // look ~1000x closer than it is: on a real track it produced a phantom
    // drop permanently "5 seconds away" that both never fired and, by
    // always counting as imminent, suppressed the quiet floor as well - so
    // nothing ever broke. director.js converts the same way at its own
    // entry point.
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    this._nowMs = now;
    if (now == null) { this._dropDueAt = null; return null; }
    // A seek, a track change or a re-anchored position smoother: re-aim
    // rather than firing every drop we appear to have "passed".
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
    }
    const ref = this._lastNowMs != null ? Math.min(this._lastNowMs, now) : now;
    this._lastNowMs = now;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] < ref - DROP_STALE_MS) this._dropIdx++;
    this._dropDueAt = this._dropIdx < this._drops.length ? this._drops[this._dropIdx] : null;
    if (this._dropDueAt == null) return null;
    return (this._dropDueAt - now) / 1000;
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    if (paused) return; // gel complet - fragments hold their pose in flight

    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;

    const director = profile.director;
    const bands = (director && director.bands) || null;
    const hasBands = !!(director && director.hasReliableGrid && bands);
    const low = hasBands ? clamp01(bands.low) : 0.5 + 0.4 * Math.sin(this._time * 0.7);
    const high = hasBands ? clamp01(bands.high) : 0.5 + 0.4 * Math.sin(this._time * 1.1 + 2.0);
    const character = (director && director.character) || null;
    const dynamics = character && character.dynamics != null ? character.dynamics : 0.5;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || DEFAULT_BPM;
    const beatS = 60 / bpm;

    // A new track resets everything and asks for a new diagram.
    if (profile.trackId !== this._lastTrackId) {
      this._lastTrackId = profile.trackId;
      this._phase = 'intact';
      this._burstPendingUntil = null;
      this._resetBodies();
      this._pendingPattern = null;
      this._genState = null;
      this._requestPattern(this._wantedSites || SITES_MIN);
    }

    // progress -> fragment count, applied to the NEXT generated diagram.
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    this._wantedSites = Math.round(lerp(SITES_MIN, this._layout.sites, Math.sin(Math.PI * progress)));

    // ?shattercells=1 - the diagram flat and still, with its seams wide
    // open and its edges lit: the one honest way to read the density
    // falloff and the radial splinters off the screen (criteria 1 and 2).
    // Nothing is ever ejected, so the physics never runs.
    if (this._debugCells) {
      this._phase = 'intact';
      const u = this._fragUniforms;
      u.uTime.value = this._time;
      u.uPlateSize.value = this._plateSize;
      u.uThickness.value = this._layout.thickness ? THICKNESS_WORLD / this._plateSize : 0;
      u.uCrack.value = 1;
      u.uVibrate.value = 0;
      u.uEdgeRim.value = 0.9;
      u.uWaveAmt.value = 0;
      this._backdropUniforms.uTime.value = this._time;
      this._backdropUniforms.uAspect.value = this._camera.aspect;
      this._adoptPendingPattern();
      return;
    }

    // --- schedule -------------------------------------------------------
    let sUntilDrop = this._updateDropSchedule(profile);
    this._nextDropMs = sUntilDrop != null ? sUntilDrop * 1000 : null;

    if (this._phase === 'intact' || this._phase === 'vibrate' || this._phase === 'crack') {
      const quietFor = this._time - this._lastBreakAt;
      // The quiet floor is ALWAYS armed, timeline or not. A scheduled drop
      // only suppresses it while that drop is actually near - see
      // MAX_QUIET_S for the track this was found on.
      const dropIsNear = sUntilDrop != null && sUntilDrop <= MAX_QUIET_S;
      const sUntilQuiet = MAX_QUIET_S - quietFor;

      // Anticipation counts down to whichever break is actually coming.
      const sAnticipate = dropIsNear ? sUntilDrop : sUntilQuiet;
      let wantPhase = 'intact';
      if (sAnticipate <= CRACK_BEATS * beatS) wantPhase = 'crack';
      else if (sAnticipate <= VIBRATE_BEATS * beatS) wantPhase = 'vibrate';
      if (wantPhase !== this._phase) {
        this._phase = wantPhase;
        this._phaseStart = this._time;
      }

      // The hit. A scheduled drop fires on the first frame at or after its
      // own timestamp (see _updateDropSchedule); otherwise the quiet floor
      // fires, brought forward onto a real energy rise when there is one.
      const forced = this._forceBreak && quietFor > 6;
      const dropNow = this._dropDueAt != null && this._nowMs != null
        && this._nowMs >= this._dropDueAt;
      // The quiet floor fires when its anticipation ends. It used to wait
      // there for a bands.low rise, up to 1.6 x MAX_QUIET_S: the pane sat
      // fully cracked and motionless for as long as eight seconds.
      const quietNow = !dropIsNear && quietFor >= MAX_QUIET_S;

      // A drop reached before the diagram is ready is KEPT, and fires as soon
      // as the fragments exist (DROP_WAIT_PATTERN_S at most).
      if (dropNow) {
        this._dropIdx++;   // never fire the same drop twice
        this._burstPendingUntil = this._time + DROP_WAIT_PATTERN_S;
      }
      const pending = this._burstPendingUntil != null && this._time <= this._burstPendingUntil;
      if ((pending || quietNow || forced) && this._count) {
        this._burstPendingUntil = null;
        this._burst(dynamics);
        // The pointer has moved to the NEXT drop: re-read it now. sUntilDrop
        // still held the drop that just fired - about zero - and the return
        // rule below read that as "the next drop is here", sending every
        // fragment home on the very frame of the burst. A drop-triggered
        // break was a crack that closed again; only the quiet-floor breaks,
        // with no drop near, ever flew.
        sUntilDrop = this._updateDropSchedule(profile);
        this._nextDropMs = sUntilDrop != null ? sUntilDrop * 1000 : null;
      }
    }
    this._lastLow = low;

    // --- slow motion ----------------------------------------------------
    if (this._forcedTimeScale != null) {
      this._timeScale = this._forcedTimeScale;
    } else if (this._phase === 'flying' || this._phase === 'reassembling') {
      const since = this._time - this._burstAt;
      this._timeScale = lerp(TIME_SCALE_BURST, TIME_SCALE_SETTLED, clamp01(since / TIME_SCALE_RAMP_S));
    } else {
      this._timeScale = TIME_SCALE_SETTLED;
    }

    // --- integrate, fixed step -----------------------------------------
    // TWO clocks, deliberately. The flight integrates on the slow-motion
    // clock - that IS the effect. The return does NOT: its per-fragment
    // durations and the "home two bars before the next drop" calibration
    // are both stated in real seconds, and running them at uTimeScale
    // stretched a 2.5-4.5s return into 6-11s of wall clock (measured: one
    // return took 10.5s) and left the calibration wrong by a factor of
    // 1/uTimeScale. Both loops are fixed-step, so both stay identical at 30
    // and 60fps (criterion 7).
    let steps = 0;
    if (this._phase === 'flying') {
      this._accumulator += dt * this._timeScale;
      while (this._accumulator >= FIXED_DT && steps < MAX_SUBSTEPS) {
        this._stepPhysics(FIXED_DT);
        this._accumulator -= FIXED_DT;
        steps++;
      }
    } else if (this._phase === 'reassembling') {
      this._returnAccumulator += dt;
      while (this._returnAccumulator >= FIXED_DT && steps < MAX_SUBSTEPS) {
        this._stepReassembly(FIXED_DT);
        this._returnAccumulator -= FIXED_DT;
        steps++;
      }
    }
    if (steps > 0) this._writeInstances();

    // Hand back in time to be whole two bars before the next drop. Only a
    // drop that is actually within reach constrains the return; a distant
    // one (or none at all) lets it run at its natural pace.
    if (this._phase === 'flying') {
      const flightS = this._time - this._burstAt;
      const leadS = REASSEMBLE_LEAD_BARS * 4 * beatS;
      const windowS = sUntilDrop != null && sUntilDrop <= MAX_QUIET_S ? sUntilDrop - leadS : null;
      const naturalDone = flightS > 3.0;
      // However close the next drop, the break is SEEN: a minimum of flight.
      if (flightS >= MIN_FLIGHT_S && ((windowS != null && windowS <= RETURN_S_NEAR) || (windowS == null && naturalDone))) {
        this._beginReassembly(windowS);
      }
    }

    // --- uniforms -------------------------------------------------------
    const u = this._fragUniforms;
    u.uTime.value = this._time;
    u.uPlateSize.value = this._plateSize;
    u.uThickness.value = this._layout.thickness ? THICKNESS_WORLD / this._plateSize : 0;
    u.uEdgeRim.value = this._phase === 'intact'
      ? 0
      : (EDGE_RIM_BASE + EDGE_RIM_BAND * high) * (this._phase === 'vibrate' ? 0.35 : 1);
    // Cracks: nothing during 'intact', appearing through 'vibrate', fully
    // spread by the drop, and gone again once every fragment is home.
    let crack = 0;
    if (this._phase === 'vibrate') crack = clamp01((this._time - this._phaseStart) / Math.max(VIBRATE_BEATS * beatS, 0.2)) * 0.45;
    else if (this._phase === 'crack') crack = 0.45 + 0.55 * clamp01((this._time - this._phaseStart) / Math.max(CRACK_BEATS * beatS, 0.2));
    else if (this._phase === 'flying') crack = 1;
    else if (this._phase === 'reassembling') crack = 1;
    u.uCrack.value = crack;
    u.uVibrate.value = (this._phase === 'vibrate' || this._phase === 'crack')
      ? VIBRATE_AMP * low * clamp01((this._time - this._phaseStart) / Math.max(VIBRATE_BEATS * beatS, 0.2) + 0.15)
      : 0;
    // The silhouette fade, in pane-UV units, from the pane's REAL size in
    // drawing-buffer pixels - which is CSS height x BASE_RENDER_SCALE x
    // pixel ratio x tier scale, not window.innerHeight (see
    // getRenderBufferHeight). A fixed UV width would be several pixels on a
    // 4K panel and a fraction of one on the Pi.
    const panePx = Math.max(8, (this._plateSize / this._visibleHeight) * getRenderBufferHeight());
    u.uEdgeAA.value = clamp(EDGE_AA_PX / panePx, 0.002, 0.06);
    u.uBeatPhase.value = profile.beatPhase != null ? profile.beatPhase : 0;
    u.uWaveAmt.value = this._phase === 'flying' && profile.beatPhase != null ? 1 : 0;

    this._backdropUniforms.uTime.value = this._time;
    this._backdropUniforms.uAspect.value = this._camera.aspect;

    // --- artwork / palette ---------------------------------------------
    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) {
        const self = this;
        loadArtworkTexture(profile.artworkUrl, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true },
          function (tex) {
            if (self._artTexture) self._artTexture.dispose();
            self._artTexture = tex;
            self._fragUniforms.uArt.value = tex;
            self._fragUniforms.uHasArt.value = 1;
          },
          function () {});
      }
    }
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const pal = profile.artworkPalette;
      const dom = pal && pal.dominantColors && pal.dominantColors.length ? pal.dominantColors[0] : 0x2a3550;
      this._backdropUniforms.uTint.value.set(dom);
      const vivid = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors[0] : 0xdfe9ff;
      this._fragUniforms.uGlassTint.value.set(vivid).lerp(new THREE.Color(0xffffff), 0.72);
    }

    if (this._debugInfo && this._time - (this._lastInfoAt || 0) > 1) {
      this._lastInfoAt = this._time;
      // eslint-disable-next-line no-console
      console.log('[shatter] phase=' + this._phase
        + ' nextDrop=' + (this._nextDropMs != null ? (this._nextDropMs / 1000).toFixed(2) + 's' : 'none')
        + ' timeScale=' + this._timeScale.toFixed(3)
        + ' fragments=' + (this._count || 0));
    }
  }

  _runPasses(renderer) {
    this._buffer.ensureSize(renderer);
    const prevTarget = renderer.getRenderTarget();
    renderer.setRenderTarget(this._buffer.write);
    renderer.render(this._backdropScene, this._ortho);
    renderer.setRenderTarget(prevTarget);

    const tex = this._buffer.write.texture;
    this._displayUniforms.uBackdrop.value = tex;
    this._fragUniforms.uBackdrop.value = tex;
    const size = renderer.getDrawingBufferSize(_scratchSize);
    this._fragUniforms.uResolution.value.set(size.x, size.y);
  }

  onResize() {
    if (!this._camera) return;
    this._recomputeWorld();
    this._fragUniforms.uPlateSize.value = this._plateSize;
    this._backdropUniforms.uAspect.value = this._camera.aspect;
  }

  dispose() {
    if (this._idleHandle != null) {
      if (typeof cancelIdleCallback === 'function') cancelIdleCallback(this._idleHandle);
      else clearTimeout(this._idleHandle);
      this._idleHandle = null;
    }
    this._genState = null;
    this._scene.remove(this._displayMesh);
    this._scene.remove(this._fragMesh);
    this._displayGeo.dispose();
    this._displayMat.dispose();
    this._backdropGeo.dispose();
    this._backdropMat.dispose();
    this._fragGeo.dispose();
    this._fragMat.dispose();
    this._buffer.dispose();
    if (this._artTexture) {
      this._artTexture.dispose();
      this._artTexture = null;
    }
  }
}

const _scratchSize = new THREE.Vector2();
const _camUp = new THREE.Vector3();
const _camRight = new THREE.Vector3();
const _driftEuler = new THREE.Euler();
const _driftQuat = new THREE.Quaternion();
const _camFwd = new THREE.Vector3();
